// src/hooks/useUserMedia.js
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Robust getUserMedia hook
 * - Soft constraints first, graceful fallbacks
 * - Back-camera detection for mobile
 * - Clean stop on unmount / camera switch
 * - Exposes switchCamera(), stopStream(), request(mode?)
 */
export default function useUserMedia(initialFacingMode = "user") {
  const [facingMode, setFacingMode] = useState(initialFacingMode); // "user" | "environment"
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const gettingRef = useRef(false);

  const stopStream = useCallback(() => {
    try {
      stream?.getTracks?.().forEach((t) => t.stop());
    } catch {}
  }, [stream]);

  const enumerateHasBackCamera = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.some(
        (d) =>
          d.kind === "videoinput" &&
          /back|rear|environment/i.test(`${d.label}`)
      );
    } catch {
      return false;
    }
  }, []);

  const request = useCallback(
    async (mode = facingMode) => {
      if (gettingRef.current) return;
      gettingRef.current = true;
      setError(null);

      try {
        const soft = {
          audio: false,
          video: {
            facingMode: { ideal: mode },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        };

        let s = await navigator.mediaDevices.getUserMedia(soft);

        const track = s.getVideoTracks()[0];
        if (!track || track.readyState !== "live") {
          throw new Error("Camera track not live; retrying with fallback.");
        }

        setStream(s);
      } catch (e1) {
        try {
          const basic = { audio: false, video: true };
          const s2 = await navigator.mediaDevices.getUserMedia(basic);
          setStream(s2);
        } catch (e2) {
          try {
            const hasBack = await enumerateHasBackCamera();
            if (mode !== "environment" && hasBack) {
              const s3 = await navigator.mediaDevices.getUserMedia({
                audio: false,
                video: { facingMode: { ideal: "environment" } },
              });
              setFacingMode("environment");
              setStream(s3);
            } else {
              throw e2;
            }
          } catch (e3) {
            setError(e3 || e2 || e1);
            setStream(null);
          }
        }
      } finally {
        gettingRef.current = false;
      }
    },
    [enumerateHasBackCamera, facingMode]
  );

  useEffect(() => {
    if (!navigator?.mediaDevices?.getUserMedia) {
      setError(new Error("getUserMedia not supported in this browser."));
      return;
    }
    request();
    return () => stopStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchCamera = useCallback(async () => {
    const next = facingMode === "user" ? "environment" : "user";
    setFacingMode(next);
    stopStream();
    await request(next);
  }, [facingMode, request, stopStream]);

  return { stream, error, facingMode, switchCamera, stopStream, request };
}
